import { defineStore } from 'pinia'

import { resolveCurrentSelection } from '@/helpers/session-defaults'
import AuthService from '@/services/auth.service'
import ProfileService from '@/services/profile.service'
import ProfileAccountService from '@/services/profile-account.service'
import ProfileFavoriteService from '@/services/profile-favorite.service'
import ProjectService from '@/services/project.service'
import FlagService from '@/services/flag.service'
import { useAccountEventsStore } from '@/stores/account-events'
import { removeBy, upsertBy } from '@/utils/list-mutations'

// Bulk deletes send one event per flag, so wait for them to settle first.
export const FLAG_REFRESH_DEBOUNCE_MS = 400

const FLAG_CHANGED_EVENTS = ['flag-stored', 'flag-updated', 'flag-destroyed']

// Per store instance: the function that removes its flag listeners.
const flagListeners = new WeakMap()

const byName = (a, b) => (a.name ?? '').localeCompare(b.name ?? '')

// Same order as the environment picker: by project name, then environment key.
const compareFavorites = (a, b) =>
    (a.project_name ?? '').localeCompare(b.project_name ?? '') ||
    (a.environment_key ?? '').localeCompare(b.environment_key ?? '')

// Favorites match on project and environment key, ignoring case, the same
// way FavoriteButton does.
const favoriteId = (favorite) =>
    JSON.stringify([
        favorite.project_key,
        favorite.environment_key,
    ]).toLowerCase()

// Return projects with the environment list of the one matching projectKey
// passed through change(). Other projects come back as they were.
const updateEnvironments = (projects, projectKey, change) =>
    projects.map((project) =>
        project.key === projectKey
            ? {
                  ...project,
                  environments: {
                      ...project.environments,
                      data: change(project.environments?.data ?? []),
                  },
              }
            : project
    )

/**
 * Session store for authentication state and account context.
 */
export const useSessionStore = defineStore('session', {
    state: () => ({
        status: { loggedIn: false },
        data: null,
    }),
    getters: {
        /**
         * Retrieve a flag value by key with a fallback.
         *
         * @param {string} key
         * @param {*} defaultValue
         * @returns {*}
         */
        getFlag: (state) => (key, defaultValue) => {
            if (state.data?.flags?.data == null) {
                return defaultValue
            }

            const flag = state.data.flags.data.find((flag) => flag.key == key)
            if (flag === undefined) {
                return defaultValue
            }

            return flag.value
        },
    },
    actions: {
        /**
         * Update the account data in state.
         *
         * @param {object} account
         * @returns {void}
         */
        setAccountData(account) {
            if (this.data?.account?.data) {
                this.data.account.data = account
                this.persist()
            }
        },
        /**
         * Update the current user's account-wide role in state, e.g. after
         * transferring account ownership downgrades the caller to admin.
         *
         * @param {string} role
         * @returns {void}
         */
        setUserEffectiveRole(role) {
            if (this.data?.user?.data) {
                this.data.user.data.effective_role = role
                this.persist()
            }
        },
        /**
         * Persist the session state.
         *
         * Session data is intentionally kept in-memory only.
         * @returns {void}
         */
        persist() {},
        /**
         * Clear persisted session data.
         *
         * @returns {void}
         */
        clearPersisted() {},
        /**
         * Set state to authenticated and persist.
         *
         * @param {object} data
         * @returns {void}
         */
        setSuccess(data) {
            this.status.loggedIn = true
            this.data = data
            this.persist()
        },
        /**
         * Set state to unauthenticated and clear persisted data.
         *
         * @returns {void}
         */
        setFailure() {
            this.status.loggedIn = false
            this.data = null
            this.clearPersisted()
        },
        /**
         * Clear local state without remote logout.
         *
         * @returns {void}
         */
        setDestroy() {
            this.status.loggedIn = false
            this.data = null
            this.clearPersisted()
        },
        /**
         * Authenticate a user and initialize session data.
         *
         * @param {object} user
         * @returns {Promise<object>}
         */
        async store(user) {
            try {
                const response = await AuthService.store(user)
                const data = response.data
                data.current = {
                    ...resolveCurrentSelection(data),
                    flags: [],
                }

                this.setSuccess(data)
                return Promise.resolve(response)
            } catch (error) {
                this.setFailure()
                return Promise.reject(error)
            }
        },
        /**
         * Destroy the session locally or remotely.
         *
         * @param {boolean} destroyRemote
         * @returns {Promise<void>}
         */
        destroy(destroyRemote) {
            if (!destroyRemote) {
                this.setDestroy()
                return Promise.resolve()
            }

            return AuthService.destroy().then(
                () => {
                    this.setDestroy()
                    return Promise.resolve()
                },
                () => {
                    this.setDestroy()
                    return Promise.resolve()
                }
            )
        },
        /**
         * Refresh the user favorites list.
         *
         * @returns {Promise<void>}
         */
        refreshFavorites() {
            return ProfileFavoriteService.list().then((data) => {
                if (this.data?.user?.data) {
                    this.data.user.data.favorites.data = data.data ?? []
                    this.persist()
                }
            })
        },
        /**
         * Refresh the project list.
         *
         * @returns {Promise<void>}
         */
        refreshProjects() {
            return ProjectService.list().then((data) => {
                if (this.data?.projects?.data) {
                    this.data.projects.data = data.data
                    this.persist()
                }
            })
        },
        /**
         * Refresh flags for the selected project.
         *
         * @param {string} projectKey
         * @returns {Promise<void>}
         */
        refreshFlags(projectKey) {
            if (!projectKey) {
                return Promise.resolve()
            }

            return FlagService.list(projectKey).then((response) => {
                if (this.data?.current) {
                    this.data.current.flags = response.data
                    this.persist()
                }
            })
        },
        /**
         * Keep the cached flags for the current project in step with flag
         * changes made through the Management API or MCP. A change to a flag in
         * the current project schedules one refetch; changes to other projects
         * are ignored. A reconnect refetches too, in case a change was missed.
         * Calling this again while listening does nothing.
         *
         * @returns {void}
         */
        listenForFlagChanges() {
            if (flagListeners.has(this)) return

            const accountEvents = useAccountEventsStore()
            let refreshTimer = null

            // refreshFlags replaces the cache with the list for the project it
            // is given, so always read the current project at refresh time.
            const refreshCurrentProject = () => {
                refreshTimer = null
                this.refreshFlags(this.data?.current?.projectKey).catch(() => {
                    // Already handled globally (e.g. an auth redirect).
                })
            }
            const scheduleRefresh = () => {
                clearTimeout(refreshTimer)
                refreshTimer = setTimeout(
                    refreshCurrentProject,
                    FLAG_REFRESH_DEBOUNCE_MS
                )
            }
            const onFlagChanged = (data) => {
                if (data?.projectKey !== this.data?.current?.projectKey) return

                scheduleRefresh()
            }

            const stops = [
                ...FLAG_CHANGED_EVENTS.map((eventName) =>
                    accountEvents.on(eventName, onFlagChanged)
                ),
                accountEvents.onResync(scheduleRefresh),
            ]

            flagListeners.set(this, () => {
                clearTimeout(refreshTimer)
                refreshTimer = null
                stops.forEach((stop) => stop())
            })
        },
        /** Stop following flag changes. The account connection stays open. */
        unlistenForFlagChanges() {
            flagListeners.get(this)?.()
            flagListeners.delete(this)
        },
        /**
         * Set the current project and environment context.
         *
         * @param {object} payload
         * @returns {Promise<void>}
         */
        setCurrent(payload) {
            if (!this.data?.current) {
                return Promise.resolve()
            }

            this.data.current.projectKey = payload.projectKey
            this.data.current.environmentKey = payload.environmentKey
            this.data.current.flags = payload.flags
            this.persist()
            return Promise.resolve()
        },
        /**
         * Replace the account data.
         *
         * @param {object} account
         * @returns {Promise<void>}
         */
        replaceAccount(account) {
            this.setAccountData(account)
            return Promise.resolve()
        },
        /**
         * Update account details.
         *
         * @param {object} account
         * @returns {Promise<object>}
         */
        updateAccount(account) {
            return ProfileAccountService.update(account).then((result) => {
                if (this.data?.account?.data) {
                    this.data.account.data = result.data
                    this.persist()
                }
                return result.data
            })
        },
        /**
         * Update the current user profile.
         *
         * @param {object} user
         * @returns {Promise<object>}
         */
        updateUser(user) {
            return ProfileService.update(user).then((result) => {
                if (this.data?.user?.data) {
                    this.data.user.data = result.data
                    this.persist()
                }
                return result.data
            })
        },
        /**
         * Validate the current session.
         *
         * @returns {Promise<object>}
         */
        validate() {
            return AuthService.validate()
        },
        /**
         * Decrement the project count in account state.
         *
         * @returns {void}
         */
        decrementProjectCount() {
            if (this.data?.account?.data) {
                this.data.account.data.count_projects =
                    this.data.account.data.count_projects - 1
                this.persist()
            }
        },
        /**
         * Increment the project count in account state.
         *
         * @returns {void}
         */
        incrementProjectCount() {
            if (this.data?.account?.data) {
                this.data.account.data.count_projects =
                    this.data.account.data.count_projects + 1
                this.persist()
            }
        },
        /**
         * Insert or replace one flag in the cached flags for the current
         * project. Pass only flags from that project.
         *
         * @param {object} flag
         * @returns {void}
         */
        upsertFlag(flag) {
            if (this.data?.current) {
                this.data.current.flags = upsertBy(
                    this.data.current.flags ?? [],
                    flag,
                    'key',
                    { position: 'sorted', compare: byName }
                )
                this.persist()
            }
        },
        /**
         * Remove one flag from the cached flags for the current project.
         *
         * @param {string} key
         * @returns {void}
         */
        removeFlag(key) {
            if (this.data?.current) {
                this.data.current.flags = removeBy(
                    this.data.current.flags ?? [],
                    key,
                    'key'
                )
                this.persist()
            }
        },
        /**
         * Insert or replace one project. Fields missing from the incoming
         * record, such as environments, keep their cached values.
         *
         * @param {object} project
         * @returns {void}
         */
        upsertProject(project) {
            if (this.data?.projects?.data) {
                const existing = this.data.projects.data.find(
                    (item) => item.key === project.key
                )
                this.data.projects.data = upsertBy(
                    this.data.projects.data,
                    existing ? { ...existing, ...project } : project,
                    'key',
                    { position: 'sorted', compare: byName }
                )
                this.persist()
            }
        },
        /**
         * Remove one project.
         *
         * @param {string} key
         * @returns {void}
         */
        removeProject(key) {
            if (this.data?.projects?.data) {
                this.data.projects.data = removeBy(
                    this.data.projects.data,
                    key,
                    'key'
                )
                this.persist()
            }
        },
        /**
         * Insert or replace one environment inside a cached project.
         *
         * @param {string} projectKey
         * @param {object} environment
         * @returns {void}
         */
        upsertEnvironment(projectKey, environment) {
            if (this.data?.projects?.data) {
                this.data.projects.data = updateEnvironments(
                    this.data.projects.data,
                    projectKey,
                    (environments) => upsertBy(environments, environment, 'key')
                )
                this.persist()
            }
        },
        /**
         * Remove one environment from a cached project.
         *
         * @param {string} projectKey
         * @param {string} envKey
         * @returns {void}
         */
        removeEnvironment(projectKey, envKey) {
            if (this.data?.projects?.data) {
                this.data.projects.data = updateEnvironments(
                    this.data.projects.data,
                    projectKey,
                    (environments) => removeBy(environments, envKey, 'key')
                )
                this.persist()
            }
        },
        /**
         * Add a favorite, or replace it if the same project and environment
         * are already favorited.
         *
         * @param {object} favorite
         * @returns {void}
         */
        addFavorite(favorite) {
            if (this.data?.user?.data?.favorites) {
                this.data.user.data.favorites.data = upsertBy(
                    this.data.user.data.favorites.data ?? [],
                    favorite,
                    favoriteId,
                    { position: 'sorted', compare: compareFavorites }
                )
                this.persist()
            }
        },
        /**
         * Remove the favorite for one project environment.
         *
         * @param {string} projectKey
         * @param {string} envKey
         * @returns {void}
         */
        removeFavorite(projectKey, envKey) {
            if (this.data?.user?.data?.favorites) {
                this.data.user.data.favorites.data = removeBy(
                    this.data.user.data.favorites.data ?? [],
                    favoriteId({
                        project_key: projectKey,
                        environment_key: envKey,
                    }),
                    favoriteId
                )
                this.persist()
            }
        },
    },
})
